import { useState } from 'react';
import { ExternalLink, Eye, Heart, ImageOff, Play, Users } from 'lucide-react';
import { PLATFORM_LABEL } from '../../../lib/reporting/socialExplorer';
import { formatWatchSeconds } from '../../../lib/reporting/socialContent';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import { ImageLightbox } from '../../../components/ui/ImageLightbox';
import { isProbablyVideoUrl } from '../../../lib/inboxView';
import type { SocialContentPost } from '../../../types/socialReporting';

// Organic Social Reporting — one IMAGE-FORWARD post card in the Content tab's Top
// posts grid. A large post image sits on top (click → a lightbox that enlarges it
// further), with the caption + per-metric figures (reach / engagement / views, +
// IG Reel watch time) underneath. Reuses the Best Post shapes.
export function ContentPostCard({ post, rank }: { post: SocialContentPost; rank?: number }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isVideo =
    post.mediaType?.toLowerCase().includes('video') ||
    post.mediaType?.toLowerCase().includes('reel') ||
    (post.thumbnailUrl ? isProbablyVideoUrl(post.thumbnailUrl) : false);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      {/* Large image — click to enlarge. */}
      {post.thumbnailUrl ? (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          title="View full size"
          className="group relative block aspect-square w-full overflow-hidden bg-slate-100"
        >
          <img
            src={post.thumbnailUrl}
            alt="Post"
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          />
          {rank !== undefined && (
            <span className="absolute left-0 top-0 rounded-br-md bg-slate-900/80 px-2 py-1 text-xs font-semibold text-white">
              #{rank}
            </span>
          )}
          {isVideo && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-black/45 p-3 text-white">
                <Play className="h-6 w-6" aria-hidden />
              </span>
            </span>
          )}
        </button>
      ) : (
        <div className="relative flex aspect-square w-full items-center justify-center bg-slate-100 text-slate-300">
          <ImageOff className="h-8 w-8" aria-hidden />
          {rank !== undefined && (
            <span className="absolute left-0 top-0 rounded-br-md bg-slate-900/80 px-2 py-1 text-xs font-semibold text-white">
              #{rank}
            </span>
          )}
        </div>
      )}

      {/* Caption + stats underneath the image. */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start gap-1.5">
          <ChannelIcon channel={post.platform} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="line-clamp-2 text-sm text-slate-700">
            {snippet(post.caption) || <span className="italic text-slate-400">No caption</span>}
          </p>
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <Stat icon={<Heart className="h-3.5 w-3.5 text-rose-500" aria-hidden />} value={`${post.engagement.toLocaleString()} eng`} strong />
          {post.reach !== null && <Stat icon={<Users className="h-3.5 w-3.5" aria-hidden />} value={`${post.reach.toLocaleString()} reach`} />}
          {post.views !== null && <Stat icon={<Eye className="h-3.5 w-3.5" aria-hidden />} value={`${post.views.toLocaleString()} views`} />}
          {post.igReelsAvgWatchTime !== null && post.igReelsAvgWatchTime > 0 && (
            <Stat icon={<Play className="h-3 w-3" aria-hidden />} value={`${formatWatchSeconds(post.igReelsAvgWatchTime)} avg watch`} />
          )}
        </div>
        {post.permalink && (
          <a
            href={post.permalink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-kyck-700 hover:underline"
          >
            View on {PLATFORM_LABEL[post.platform]}
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        )}
      </div>

      {lightboxOpen && post.thumbnailUrl && (
        <ImageLightbox url={post.thumbnailUrl} alt="Post" onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}

function Stat({ icon, value, strong }: { icon: React.ReactNode; value: string; strong?: boolean }) {
  return (
    <span className={strong ? 'inline-flex items-center gap-1 font-medium text-slate-700' : 'inline-flex items-center gap-1 text-slate-500'}>
      {icon}
      {value}
    </span>
  );
}

function snippet(caption: string, max = 120): string {
  const c = caption.trim().replace(/\s+/g, ' ');
  return c.length <= max ? c : `${c.slice(0, max - 1).trimEnd()}…`;
}
